// PermissionContext - Per-GMB profile plan-based gating
// 
// ACCESS FLOW:
// 1. User logs in → Can see dashboard/overview (always allowed)
// 2. User selects a GMB profile → Plan is fetched from backend (gmb_accounts.plan)
// 3. Features are gated based on the selected GMB's plan (none/basic/advance/pro)
// 4. canAccessFeature(featureName) → checks if GMB's plan includes that feature
//
// NO email whitelist - access is purely based on the GMB profile's assigned plan

import React, { createContext, useContext, useState, useCallback } from 'react';
import { getSession } from '../utils/authUtils';
import { useProfilePlan } from './ProfilePlanContext';

const PermissionContext = createContext(null);

// Admin role IDs - only role_id 9 is admin
const SUPER_ADMIN_ROLE_ID = 9;
const ADMIN_ROLE_IDS = [9];

export const PermissionProvider = ({ children }) => {
  const session = getSession();
  const user = session?.user || session;
  const roleId = user?.role_id || user?.roleId;
  const { hasFeature, planCode, hasSubscription } = useProfilePlan();
  
  const [upgradeNotice, setUpgradeNotice] = useState(null);
  
  const isSuperAdmin = roleId === SUPER_ADMIN_ROLE_ID;
  const isAdmin = ADMIN_ROLE_IDS.includes(Number(roleId));
  
  // hasPlanAccess: true if the currently selected GMB has a plan assigned (basic, advance, or pro)
  const hasPlanAccess = hasSubscription && planCode;
  
  // Trigger upgrade notice (for sidebar)
  const triggerUpgradeNotice = useCallback((message) => {
    setUpgradeNotice(message);
    setTimeout(() => setUpgradeNotice(null), 3000);
  }, []);

  // Check if user can access a specific feature based on GMB's plan
  const canAccessFeature = useCallback((feature) => {
    // Admins can access everything
    if (isAdmin) return true;
    // Check the GMB's plan for this feature
    return hasFeature(feature);
  }, [isAdmin, hasFeature]);

  const value = {
    loading: false,
    // hasPlanAccess: true if selected GMB has a plan (basic/advance/pro)
    hasPlanAccess,
    // For backwards compatibility - same as hasPlanAccess or isAdmin
    hasFullAccess: isAdmin || hasPlanAccess,
    userEmail: user?.email || null,
    isSuperAdmin,
    isAdmin,
    planCode,
    // canAccessFeature: main method - checks GMB's plan for feature access
    canAccessFeature,
    canManagePermissions: () => isAdmin,
    triggerUpgradeNotice,
    upgradeNotice,
  };

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    return {
      loading: false,
      hasPlanAccess: false,
      hasFullAccess: false,
      userEmail: null,
      isSuperAdmin: false,
      isAdmin: false,
      planCode: null,
      canAccessFeature: () => false,
      canManagePermissions: () => false,
      triggerUpgradeNotice: () => {},
      upgradeNotice: null,
    };
  }
  return ctx;
};

export default PermissionContext;
